import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Breadcrumb } from '@boozallen-uip/uswds-react-core';
import PageHeader from '../../components/PageHeader/PageHeader.component';
import AccessStatus from '../../components/AccessStatus.component';
import { PageSubHeader, SectionContainer } from '../shared.styled';
import { UserContext } from '../UserContext';
import useAnalytics from '../../hooks/useAnalytics.hook';
import './Analytics.page.css';
import {
  ACCESS_DENIED,
  ACCESS_NO_LOGIN,
  ACCESS_WAITING,
  BASE_TITLE,
  HOME_URL,
  ANALYTICS_URL
} from '../../utlities/core.constants';

export default function AnalyticsPage() {
  const { hasPendingRequestAccess, checkEntityAccessLevel, hasEntityAccess, isLoggedIn, isSuperUser, user } = useContext(UserContext);
  const { getAnalytics } = useAnalytics();
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsRequestFailed, setAnalyticsRequestFailed] = useState(false);

  document.title = `${BASE_TITLE} - Analytics`;

  useEffect(() => {
    (async () => {
      const response = await getAnalytics();
      if (!response?.ok) {
        setAnalyticsRequestFailed(true);
        setAnalyticsData(null);
        return;
      }

      setAnalyticsRequestFailed(false);
      setAnalyticsData(response.data);
    })();
  }, [getAnalytics]);

  const getMetricValue = (metric) => {
    if (!metric) {
      return '--';
    }

    if (metric.value === null || metric.value === undefined) {
      return '--';
    }

    return metric.value.toLocaleString();
  };

  const formatGeneratedAt = (generatedAtUtc) => {
    if (!generatedAtUtc) {
      return '--';
    }

    const generatedAtDate = new Date(generatedAtUtc);
    if (Number.isNaN(generatedAtDate.getTime())) {
      return '--';
    }

    return generatedAtDate.toLocaleString();
  };

  const activeBuyerRaw = analyticsData?.activeBuyers?.value;
  const activeVendorRaw = analyticsData?.activeVendors?.value;
  const isTotalAcrsUnavailable = analyticsData?.totalAcrs?.available === false;
  const isAcrsLast30DaysUnavailable = analyticsData?.acrsLast30Days?.available === false;
  const isActiveBuyersUnavailable = analyticsData?.activeBuyers?.available === false;
  const isActiveVendorsUnavailable = analyticsData?.activeVendors?.available === false;
  const isTotalProductsUnavailable = analyticsData?.totalProducts?.available === false;
  const isActiveUsersUnavailable = isActiveBuyersUnavailable || isActiveVendorsUnavailable;
  const areAllMetricsUnavailable = analyticsRequestFailed || (isTotalAcrsUnavailable
    && isAcrsLast30DaysUnavailable
    && isActiveUsersUnavailable
    && isTotalProductsUnavailable);
  const hasAnyUnavailableMetrics = analyticsRequestFailed || isTotalAcrsUnavailable
    || isAcrsLast30DaysUnavailable
    || isActiveUsersUnavailable
    || isTotalProductsUnavailable;

  const activeBuyerValue = getMetricValue(analyticsData?.activeBuyers);
  const activeVendorValue = getMetricValue(analyticsData?.activeVendors);
  const hasNumericActiveBuyer = typeof activeBuyerRaw === 'number';
  const hasNumericActiveVendor = typeof activeVendorRaw === 'number';
  const activeUsersTotal = hasNumericActiveBuyer || hasNumericActiveVendor
    ? (hasNumericActiveBuyer ? activeBuyerRaw : 0) + (hasNumericActiveVendor ? activeVendorRaw : 0)
    : null;

  const analyticsMetrics = useMemo(() => ([
    {
      title: 'Total # of ACRs',
      value: getMetricValue(analyticsData?.totalAcrs),
      context: 'All-time',
      unavailable: isTotalAcrsUnavailable
    },
    {
      title: 'ACRs added in last 30 days',
      value: getMetricValue(analyticsData?.acrsLast30Days),
      context: 'Rolling 30-day window',
      unavailable: isAcrsLast30DaysUnavailable
    },
    {
      title: '# of active users',
      value: activeUsersTotal === null ? '--' : activeUsersTotal.toLocaleString(),
      context: `Buyers: ${activeBuyerValue} | Vendors: ${activeVendorValue}`,
      unavailable: isActiveUsersUnavailable
    },
    {
      title: '# of total products',
      value: getMetricValue(analyticsData?.totalProducts),
      context: 'Current product count',
      unavailable: isTotalProductsUnavailable
    }
  ]), [
    activeBuyerValue,
    activeUsersTotal,
    activeVendorValue,
    analyticsData,
    isAcrsLast30DaysUnavailable,
    isActiveUsersUnavailable,
    isTotalAcrsUnavailable,
    isTotalProductsUnavailable
  ]);

  if (!isLoggedIn()) {
    return <AccessStatus access={ACCESS_NO_LOGIN} />;
  }

  if (!checkEntityAccessLevel() || !hasEntityAccess() || !isSuperUser) {
    return <AccessStatus access={ACCESS_DENIED} />;
  }

  if (hasPendingRequestAccess) {
    return <AccessStatus access={ACCESS_WAITING} />;
  }

  return (
    <div>
      <PageHeader imageUrl="/page-banners/account_hero_2x.webp" backgroundSize='2800px 391px' className='2xl:!bg-cover'>
        Welcome, {user?.name || user?.email}
      </PageHeader>
      <SectionContainer>
        <div className='mb-6'>
          <Breadcrumb links={[{ href: HOME_URL, text: 'Home' }, { href: ANALYTICS_URL, text: 'Analytics' }]} />
        </div>
        <PageSubHeader>Analytics</PageSubHeader>
        <p className='leading-normal mb-6'>
          View summary analytics for ACR Repository activity.
        </p>
        {hasAnyUnavailableMetrics ? (
          <div className='mb-4 analytics-unavailable-alert'>
            <Alert
              type="warning"
              heading={areAllMetricsUnavailable ? 'Analytics data unavailable' : 'Partial data unavailable'}
            >
              {areAllMetricsUnavailable
                ? 'All analytics metrics are temporarily unavailable. Please try again later.'
                : 'Some analytics metrics are temporarily unavailable. Available metrics are still shown.'}
            </Alert>
          </div>
        ) : null}
        <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4'>
          {analyticsMetrics.map((metric) => (
            <div
              key={metric.title}
              className='rounded-md border border-base-lighter bg-base-lightest p-4 shadow-sm'
            >
              <p className='text-base-dark margin-0 text-sm font-bold mb-2'>
                {metric.title}
              </p>
              <p className='margin-0 text-4xl font-bold leading-tight mb-1'>
                {metric.value}
              </p>
              <p className='margin-0 text-base-dark text-xs'>
                {metric.context}
              </p>
              {metric.unavailable ? (
                <div className='mt-2'>
                  <span className='inline-block rounded bg-base-lighter px-2 py-1 text-[11px] font-bold text-base-dark'>
                    Unavailable
                  </span>
                  <p className='margin-0 text-base-dark text-xs mt-1'>
                    Data temporarily unavailable.
                  </p>
                </div>
              ) : null}
            </div>
          ))}
        </div>
        <p className='text-xs text-base-dark mt-3 mb-0'>
          Last updated: {formatGeneratedAt(analyticsData?.generatedAtUtc)}
        </p>
      </SectionContainer>
    </div>
  );
}
